import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

export default function EmailDiagnosticsSection({ user }) {
  const [status, setStatus] = useState(null);
  const [to, setTo] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/email-status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false, sender: "?", mode: "stub" }));
  }, []);

  const send = async () => {
    if (!to.trim()) { toast.error("Recipient email is required"); return; }
    setSending(true); setResult(null);
    try {
      const r = await api.post("/admin/test-email", { to: to.trim() });
      setResult({ ok: true, ...r.data });
      toast.success(`Sent to ${r.data.to}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to send";
      setResult({ ok: false, error: detail });
      toast.error(detail);
    } finally { setSending(false); }
  };

  return (
    <section data-testid="section-email">
      <h2 className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: "Outfit" }}>Email</h2>
      <p className="text-xs text-slate-500 mb-5">
        Sender configuration for transactional email (mentions, leave decisions, compliance reminders, Firm Brief).
      </p>

      <div className="border border-slate-200 p-5 rounded-sm space-y-4">
        <div className="flex items-center gap-3 text-xs">
          <Mail size={14} className="text-slate-500" />
          {status === null ? (
            <span className="text-slate-400">Checking…</span>
          ) : (
            <>
              <span className="gc-label">Status</span>
              {status.mode === "live" ? (
                <span className="status-pill status-completed" data-testid="email-status">Live</span>
              ) : (
                <span className="status-pill status-cancelled" data-testid="email-status">Stub</span>
              )}
              <span className="gc-label ml-3">Sender</span>
              <span className="font-mono-data text-[#0a2540]" data-testid="email-sender">{status.sender}</span>
            </>
          )}
        </div>

        {status?.mode === "stub" && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
            RESEND_API_KEY is not set — emails are being logged as <span className="font-mono-data">[email-stub]</span>. Add the key in <span className="font-mono-data">backend/.env</span> and restart.
          </div>
        )}

        <div>
          <Label className="text-xs">Send test email to</Label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="you@griffinconsultancy.in"
              className="rounded-sm flex-1 min-w-[240px] max-w-md"
              data-testid="input-test-email-to"
            />
            <Button
              onClick={send}
              disabled={sending || !status?.configured}
              className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm"
              data-testid="btn-send-test-email"
            >
              <Send size={14} className="mr-1.5" /> {sending ? "Sending…" : "Send test email"}
            </Button>
          </div>
          <div className="mt-1 text-[10px] text-slate-400">
            Fires a single email through Resend using the sender above. If the domain isn't verified on resend.com yet, you'll see an actionable error message here.
          </div>
        </div>

        {result && (
          <div
            className={`border rounded-sm px-3 py-2 text-xs ${
              result.ok
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-red-200 bg-red-50 text-red-700"
            }`}
            data-testid="test-email-result"
          >
            {result.ok ? (
              <>
                ✅ Sent from <span className="font-mono-data">{result.sender}</span> → <span className="font-mono-data">{result.to}</span>. Check the inbox (and spam, just in case).
              </>
            ) : (
              <>
                ⚠️ {result.error}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
